import { Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { ProjectsLayout } from '../../layouts/projects-layout'
import { getDateLocale } from '@/lib/i18n'

interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  organization_role: string | null
  avatar_url: string | null
  initials: string
  has_avatar: boolean
  created_at: string
  last_sign_in_at: string | null
}

interface ProfileShowProps {
  user: User
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Show({ user }: ProfileShowProps) {
  const { t } = useTranslation('profile')

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('accountInfo.never')
    return new Date(dateString).toLocaleDateString(getDateLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatRole = (role: string | null) => {
    if (!role) return t('accountInfo.notAvailable')
    return role.charAt(0).toUpperCase() + role.slice(1)
  }

  return (
    <>
      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">

        {/* Profile Card */}
        <Card className="overflow-hidden shadow-lg border">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-primary/80 to-primary/70 px-6 py-8 relative">
            <div className="absolute inset-0 bg-black/20"></div>
            <div className="flex items-center gap-4 relative z-10">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={`${user.first_name} ${user.last_name}`}
                  className="w-20 h-20 rounded-full object-cover shadow-xl ring-4 ring-white/20 dark:ring-white/10"
                />
              ) : (
                <div className="w-20 h-20 bg-card text-primary rounded-full flex items-center justify-center text-2xl font-bold shadow-xl ring-4 ring-white/20 dark:ring-white/10">
                  {user.initials}
                </div>
              )}
              <div className="text-white">
                <h2 className="text-2xl font-bold drop-shadow-lg">
                  {user.first_name} {user.last_name}
                </h2>
                <p className="text-white/90 drop-shadow">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-6">
              <CardTitle>{t('accountInfo.title')}</CardTitle>
              <Button asChild>
                <Link href="/profile/edit">{t('actions.edit')}</Link>
              </Button>
            </div>

            <div className="space-y-6">
              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    {t('accountInfo.firstName')}
                  </label>
                  <p className="text-foreground font-medium">{user.first_name}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    {t('accountInfo.lastName')}
                  </label>
                  <p className="text-foreground font-medium">{user.last_name}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    {t('accountInfo.email')}
                  </label>
                  <p className="text-foreground font-medium">{user.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    {t('accountInfo.organizationRole')}
                  </label>
                  <Badge variant="secondary">
                    {formatRole(user.organization_role)}
                  </Badge>
                </div>
              </div>

              {/* Account Details */}
              <div className="border-t pt-6">
                <h4 className="text-md font-semibold text-foreground mb-4">{t('accountDetails.title')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      {t('accountInfo.memberSince')}
                    </label>
                    <p className="text-foreground">{formatDate(user.created_at)}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      {t('accountInfo.lastSignIn')}
                    </label>
                    <p className="text-foreground">{formatDate(user.last_sign_in_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <ProjectsLayout>{page}</ProjectsLayout>

export default Show
